<script setup lang="ts">
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import { getPopoverArrowStyle } from '@/utils/dom.utils';
import { EPosition } from 'common/enum/misc.enum';
import { computed, nextTick, ref, useTemplateRef } from 'vue';

const props = defineProps<{ anchorElement: HTMLElement | null; accessibleLabel: string }>();
const emit = defineEmits<{ hide: [] }>();
const ratingPopoverContentRef = useTemplateRef<HTMLElement>('ratingPopoverContentRef');
const ratingPopoverRef = useTemplateRef<InstanceType<typeof PositionablePopover>>('ratingPopoverRef');
const resolvedPosition = ref<EPosition>(EPosition.TOP_LEFT);
const arrowStyle = ref<Record<string, string>>({});

const isOpeningBelow = computed<boolean>(() => {
  return [EPosition.BOTTOM_CENTER, EPosition.BOTTOM_LEFT, EPosition.BOTTOM_RIGHT].includes(resolvedPosition.value);
});

const handlePositioned = async (position: EPosition): Promise<void> => {
  resolvedPosition.value = position;
  await nextTick();
  arrowStyle.value = getPopoverArrowStyle(props.anchorElement, ratingPopoverContentRef.value, 28);
};

defineExpose({
  show: () => ratingPopoverRef.value?.show(),
  hide: () => ratingPopoverRef.value?.hide(),
});
</script>

<template>
  <PositionablePopover
    ref="ratingPopoverRef"
    :anchor-element="anchorElement"
    role="dialog"
    :aria-label="accessibleLabel"
    content-style="!overflow-visible !rounded-group"
    dialog-style="!overflow-visible !rounded-group !bg-transparent"
    flip-strategy="both"
    :offset-y="20"
    :offset-x="15"
    :position="EPosition.TOP_LEFT"
    :shadow="false"
    @hide="emit('hide')"
    @positioned="handlePositioned"
  >
    <div
      ref="ratingPopoverContentRef"
      class="relative box-border w-max max-w-[calc(100vw-32px)] rounded-group bg-surface-primary p-md shadow-popup"
      @click.stop
    >
      <div
        :class="[
          'pointer-events-none absolute size-[28px] rotate-45 rounded-checkbox bg-surface-primary',
          isOpeningBelow ? 'top-[-10px]' : 'bottom-[-10px]',
        ]"
        :style="arrowStyle"
      />
      <div class="relative z-1 w-full max-w-full p-[3px]">
        <slot />
      </div>
    </div>
  </PositionablePopover>
</template>
